import { useTranslation } from 'react-i18next';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';

interface CancelSubscriptionDialogProps {
  onConfirm: () => void;
  activeUntil: string | null;
  isLoading?: boolean;
  disabled?: boolean;
}

export function CancelSubscriptionDialog({
  onConfirm,
  activeUntil,
  isLoading = false,
  disabled = false,
}: CancelSubscriptionDialogProps) {
  const { t, i18n } = useTranslation();
  const formattedActiveUntil = (() => {
    if (!activeUntil) {
      return '—';
    }
    const parsed = new Date(activeUntil);
    if (Number.isNaN(parsed.getTime())) {
      return '—';
    }
    return new Intl.DateTimeFormat(i18n.language ?? 'en', { dateStyle: 'medium' }).format(parsed);
  })();

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="outline" disabled={disabled}>
          {t('customer.planManagement.actions.cancelSubscription')}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('customer.planManagement.dialogs.cancelTitle')}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('customer.planManagement.dialogs.cancelDescription')}
          </AlertDialogDescription>
          <AlertDialogDescription>
            {t('customer.planManagement.dialogs.cancelActiveUntil', { date: formattedActiveUntil })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t('customer.planManagement.actions.keepCurrent')}</AlertDialogCancel>
          <AlertDialogAction asChild>
            <Button variant="destructive" onClick={onConfirm} disabled={isLoading}>
              {isLoading
                ? t('customer.planManagement.actions.processing')
                : t('customer.planManagement.actions.confirmCancel')}
            </Button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
